import { SignInButton, SignedIn, SignedOut, useAuth } from "@clerk/clerk-react";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { resolveClerkPublishableKey } from "../clerk-env";
import { fetchCustomerOrders, type CustomerOrderListItem } from "../lib/fetch-customer-orders";
import { formatCentsAsUsd } from "../lib/format-menu";

function formatOrderWhen(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

/**
 * Per redline 08 flag 7: 3 buckets + cancelled + failed. Active orders
 * (`dispatched` / `in_progress`) need to outshine archived ones in a list
 * — so the user can tell which one to track right now.
 */
function statusPillCls(status: CustomerOrderListItem["status"]): string {
  switch (status) {
    case "pending":
    case "confirmed":
      // early
      return "bg-[var(--surface-sage)] text-[var(--green-dark)]";
    case "dispatched":
    case "in_progress":
      // active — bolder
      return "bg-[var(--green)] text-white";
    case "completed":
      // archived — muted outlined
      return "border border-[var(--card-border)] text-[var(--text-muted)]";
    case "cancelled":
      return "bg-amber-50 text-amber-900";
    case "failed":
      return "bg-[var(--status-error-bg)] text-[var(--status-error-text)]";
  }
}

function statusLabel(status: CustomerOrderListItem["status"]): string {
  switch (status) {
    case "pending":
      return "Placed";
    case "confirmed":
      return "Confirmed";
    case "dispatched":
    case "in_progress":
      return "Out for delivery";
    case "completed":
      return "Delivered";
    case "cancelled":
      return "Cancelled";
    case "failed":
      return "Failed";
  }
}

function IconOrders({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20 13V7a2 2 0 00-2-2H6a2 2 0 00-2 2v6m16 0v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
      />
    </svg>
  );
}

function Header() {
  return (
    <header className="space-y-2 border-b border-[var(--card-border)] pb-6">
      <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--green)]">Account</p>
      <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">Your orders</h1>
      <p className="text-sm text-[var(--text-secondary)]">Recent and past Drop deliveries.</p>
    </header>
  );
}

export function AccountOrdersScreen() {
  const clerkPk = resolveClerkPublishableKey();
  if (!clerkPk) {
    return (
      <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6 pb-16">
        <Header />
        <div
          role="alert"
          className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          Set <span className="font-mono">VITE_CLERK_PUBLISHABLE_KEY</span> to load your orders.
        </div>
      </div>
    );
  }
  return <AccountOrdersInner />;
}

function AccountOrdersInner() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [orders, setOrders] = useState<CustomerOrderListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const data = await fetchCustomerOrders(getToken, { limit: 50, offset: 0 });
        if (!cancelled) setOrders(data.orders);
      } catch (e) {
        if (!cancelled) {
          if (e instanceof Error && /^orders_list_http_(\d+)$/.test(e.message)) {
            const code = Number(/^orders_list_http_(\d+)$/.exec(e.message)?.[1]);
            setError(
              code === 401
                ? "Sign in again — the API rejected your session."
                : code === 403
                  ? "This account cannot load customer orders."
                  : `Could not load orders (HTTP ${code}).`,
            );
          } else {
            setError("Could not load orders. Check the API and try again.");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, getToken]);

  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6 pb-16">
      <Header />

      <SignedOut>
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 text-center shadow-cardMd">
          <p className="text-sm text-[var(--text-secondary)]">Sign in to see your orders.</p>
          <SignInButton mode="modal">
            <button
              type="button"
              className="mt-4 inline-flex items-center rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)]"
            >
              Sign in
            </button>
          </SignInButton>
        </div>
      </SignedOut>

      <SignedIn>
        {loading ? (
          <p className="text-sm text-[var(--text-muted)]" aria-busy="true">
            Loading…
          </p>
        ) : error ? (
          <div
            role="alert"
            className="rounded-2xl border border-[var(--badge-red)] bg-[var(--status-error-bg)] p-4 text-sm text-[var(--status-error-text)]"
          >
            {error}
          </div>
        ) : orders != null && orders.length === 0 ? (
          <div className="rounded-2xl border border-[var(--card-border)] bg-white p-10 text-center shadow-cardMd">
            <IconOrders className="mx-auto h-12 w-12 text-[var(--text-muted)]" />
            <p className="mt-4 text-base font-semibold text-[var(--text-primary)]">No orders yet</p>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Browse a shop menu and check out to place your first order.
            </p>
            <Link
              to="/"
              className="mt-4 inline-flex items-center rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)]"
            >
              Browse the marketplace
            </Link>
          </div>
        ) : orders != null ? (
          <ul className="space-y-3">
            {orders.map((o) => (
              <li
                key={o.id}
                className="flex flex-col gap-3 rounded-2xl border border-[var(--card-border)] bg-white p-5 shadow-cardMd sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-[var(--text-primary)]">{o.dispensary.displayName}</p>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusPillCls(o.status)}`}
                    >
                      {statusLabel(o.status)}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--text-muted)]">{formatOrderWhen(o.createdAt)}</p>
                </div>
                <div className="flex items-center gap-4 sm:flex-col sm:items-end sm:gap-1">
                  <p className="text-base font-bold text-[var(--text-primary)]">{formatCentsAsUsd(o.totalCents)}</p>
                  {o.dispensary.publicSlug ? (
                    <Link
                      to="/$dispensarySlug/orders/$orderId"
                      params={{ dispensarySlug: o.dispensary.publicSlug, orderId: o.id }}
                      className="text-xs font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--green)]"
                    >
                      Track →
                    </Link>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </SignedIn>

      <div className="border-t border-[var(--card-border)] pt-6">
        <Link
          to="/"
          className="text-sm font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--green)]"
        >
          ← Back to marketplace
        </Link>
      </div>
    </div>
  );
}
